"use client";

import React from "react";
import { Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
function InnerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 py-12 text-center animate-in fade-in duration-500">
        <div className="rounded-md bg-primary/20 p-3">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-2xl font-medium text-foreground">You're Subscribed</h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
      </div>;
  }
  const inputStyles = "border-0 border-b border-border rounded-md bg-transparent px-0 shadow-md focus-visible:ring-0 focus-visible:border-primary text-base py-2 h-auto";
  return <Form onSubmit={handleSubmit} className="space-y-8 w-full max-w-md mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">First Name</FormLabel>
              <FormControl>
                <Input placeholder="First Name" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Last Name" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="Email Address" className={inputStyles} {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">Weekly devotionals and new release updates.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="Phone Number (Optional)" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm text-muted-foreground">I'm most interested in</FormLabel>
            <FormControl>
              <select className={`w-full appearance-none outline-none transition-colors ${inputStyles} text-foreground cursor-pointer`} {...field}>
                <option value="" disabled>Select an option</option>
                <option value="Bible Studies">Bible Studies</option>
                <option value="Devotionals">Devotionals</option>
                <option value="Study Guides">Study Guides</option>
                <option value="New Releases">New Releases</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree_terms" rules={{
      required: "You must agree to receive marketing emails"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
            <FormControl>
              <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-md border border-primary text-primary focus:ring-primary accent-primary" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-muted-foreground cursor-pointer">
                I agree to receive updates from Dustin Royer
              </FormLabel>
              <FormDescription className="text-xs text-muted-foreground">
                You can unsubscribe at any time
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive border border-destructive/20 bg-destructive/10 rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base tracking-wide rounded-md shadow-md">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Subscribing...
          </> : "Subscribe"}
      </Button>
    </Form>;
}
export default function JoinThe1220Family() {
  return <section data-section-id="226877" id="stay-rooted" className="dark bg-background text-foreground relative">
      <div className="absolute inset-0 bg-primary/5" aria-hidden="true" />
      
      <div className="relative mx-auto max-w-3xl px-6 py-24 sm:py-32 lg:px-8">
        <div className="text-center mb-12 space-y-4">
          <h2 className="font-default text-3xl font-medium tracking-tight text-foreground sm:text-4xl">
            Stay Rooted
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Get weekly devotionals and new release updates directly to your inbox.
          </p>
        </div>

        <div className="bg-card/80 backdrop-blur-sm p-8 sm:p-12 rounded-md shadow-lg border border-border/50">
          <FormProvider formKey="stay_rooted_form_resources" formId="stay_rooted_form_resources" sectionName="stay-rooted" defaultValues={{
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          interest: "",
          agree_terms: false
        }} validationRules={{
          first_name: {
            required: true
          },
          last_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          agree_terms: {
            required: true
          }
        }} submitText="Subscribe" successMessage="Thank you for subscribing. Check your inbox for your first devotional soon.">
            <InnerForm />
          </FormProvider>
        </div>
      </div>
      <Toaster />
    </section>;
}